import { useState } from 'react'
import { usePlanPermissions } from '../../../hooks/usePlanPermissions'
import { USE_NEW_PLAN_LOGIC } from '../../utils/constant'
import ChangePlanModal from './ChangePlanModal'
import ContractAddonsModal from './ContractAddonsModal'
import CustomButton from './CustomButton'

const PlanRestrictedContent = ({ 
  children, 
  requiredPermission, 
  requiredSupport,
  requiredReport,
  requiredDashboard,
  dashboardType,
  message = 'Esta funcionalidad no está disponible en tu plan actual.',
  className = '',
  canAddAddon = false
}) => {
  const [showChangePlanModal, setShowChangePlanModal] = useState(false)
  const [showAddonsModal, setShowAddonsModal] = useState(false)
  const { hasPermission, hasSupportAccess, hasReportAccess, hasDashboardAccess, hasPlan, subscriptionExpired, canAccessDashboard, canContractAddons } = usePlanPermissions()

  if (!USE_NEW_PLAN_LOGIC) {
    return <>{children}</>
  }

  const hasAccess = () => {
    if (!hasPlan) return false
    if (subscriptionExpired) return false
    
    if (dashboardType) {
      return canAccessDashboard(dashboardType)
    }
    
    if (requiredPermission) {
      return hasPermission(requiredPermission)
    }
    
    if (requiredSupport) {
      return hasSupportAccess(requiredSupport)
    }
    
    if (requiredReport) {
      return hasReportAccess(requiredReport)
    }
    
    if (requiredDashboard) {
      return hasDashboardAccess(requiredDashboard)
    }
    
    return true
  }

  const access = hasAccess()

  const handleChangePlan = () => {
    setShowChangePlanModal(true)
  }

  const handleOpenAddons = () => {
    setShowAddonsModal(true)
  }

  const handlePlanUpdated = () => {
    setShowChangePlanModal(false)
    setShowAddonsModal(false)
  }

  if (access) {
    return <>{children}</>
  }

  return (
    <>
      <div className={`relative ${className}`}>
        <div className="opacity-50 pointer-events-none select-none">
          {children}
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm rounded-lg p-6 z-10">
          <div className="text-center max-w-md">
            <div className="mb-4">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Funcionalidad no disponible
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {message}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {canAddAddon && canContractAddons && (
                <CustomButton
                  title="Contratar Add-on"
                  color="accent2"
                  className="px-6 py-2 rounded-full"
                  onClick={handleOpenAddons}
                />
              )}
              <CustomButton
                title="Cambiar Plan"
                color="accent2"
                className="px-6 py-2 rounded-full"
                onClick={handleChangePlan}
              />
            </div>
          </div>
        </div>
      </div>
      
      <ChangePlanModal
        isOpen={showChangePlanModal}
        onClose={() => setShowChangePlanModal(false)}
        onPlanUpdated={handlePlanUpdated}
        filterPlanBy={{
          ...(requiredPermission && { permission: requiredPermission }),
          ...(requiredSupport && { support: requiredSupport }),
          ...(requiredReport && { report: requiredReport }),
          ...(requiredDashboard && { dashboard: requiredDashboard }),
        }}
      />
      <ContractAddonsModal
        isOpen={showAddonsModal}
        onClose={() => setShowAddonsModal(false)}
        onSuccess={handlePlanUpdated}
        preferredAddonPermission={requiredPermission}
      />
    </>
  )
}

export default PlanRestrictedContent
